import { scaleLinear, scaleTime } from 'd3-scale'
import { area, curveLinear, line } from 'd3-shape'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
import { PointMarker } from './PointMarker'
import { XTick } from './XTick'
import { YTick } from './YTick'

const ChartData = require('./data.json')

const XTicks = [0, 3, 6, 9, 12]
const YTicks = [
  '2021-03-01',
  '2021-06-01',
  '2021-09-01',
  '2021-12-01',
  '2022-03-01',
  '2022-06-01',
  '2022-09-01',
  '2022-12-01',
  '2023-03-01',
]

export const Chart = ({
  lang = 'de',
  currentDate,
  pointsToMark,
  getYPosition,
}: {
  lang: 'de' | 'fr'
  currentDate: string
  pointsToMark: string[]
  getYPosition: (e: number) => void
}) => {
  const height = 6000
  const [container, { width }] = useElementSize<HTMLDivElement>()
  const [marker, setMarker] = useState<DataPoint>()

  const x = useMemo(
    () => scaleLinear().domain([0, 14]).range([0, width]),
    [width]
  )
  const y = useMemo(
    () =>
      scaleTime()
        .domain([new Date('2021-02-01'), new Date('2023-03-20')])
        .range([0, height]),
    [height]
  )
  useEffect(() => {
    let v = ChartData.find((d: DataPoint) => d.d === currentDate)
    if (v) {
      setMarker(v)
    }
    getYPosition(y(new Date(currentDate)))
  }, [currentDate, getYPosition, y])
  const a = useMemo(
    () =>
      area<{ d: number; v: number }>()
        .curve(curveLinear)
        .x1((d) => x(d.v))
        .x0(x(-10))
        .y0(y(new Date('2021-01-04')))
        .y1((d) => y(new Date(d.d))),
    [x, y]
  )
  const l = useMemo(
    () =>
      line<{ d: number; v: number }>()
        .curve(curveLinear)
        .x((d) => x(d.v))
        .y((d) => y(new Date(d.d))),
    [x, y]
  )

  const DrawDataPath =
    useMemo(
      () =>
        a([
          { d: new Date('2023-03-15').toISOString().split('T')[0], v: -10 },
          ...ChartData,
        ]),
      [a]
    ) || ''

  const DrawDataLine = useMemo(() => l(ChartData), [l]) || ''

  let titleRight = new Date(currentDate) > new Date('2022-04-01')
  let keytext =
    lang === 'de' ? (
      <>
        CS-Aktienkurs
        <br />
        in Franken
      </>
    ) : (
      <>
        Cours de l'action CS
        <br />
        en francs suisses
      </>
    )
  return (
    <ChartRoot ref={container}>
      <XKey>
        <KeyTitle style={{ textAlign: titleRight ? 'right' : 'left' }}>
          {keytext}
        </KeyTitle>
        <SVG
          style={{
            width,
            height: 30,
          }}
        >
          {XTicks.map((tick, i) => (
            <XTick key={'xtick_' + i} x={x(tick)} y1={0} y2={30} label={tick} />
          ))}
        </SVG>
      </XKey>
      <SVG style={{ height }}>
        <defs>
          <linearGradient
            x1='50%'
            y1='0%'
            x2='50%'
            y2='100%'
            id='ffflux-gradient'
          >
            <stop
              stopColor='var(--text-color)'
              stopOpacity='0.1'
              offset='0%'
            ></stop>

            <stop
              stopColor='var(--red-main)'
              stopOpacity='1'
              offset='100%'
            ></stop>
          </linearGradient>
        </defs>
        <g className='x-ticks'></g>
        <g className='area'>
          <AreaPath
            d={DrawDataPath}
            style={{
              fill: 'url(#ffflux-gradient)',
            }}
          />
          <g className='y-ticks'>
            {YTicks.map((tick, i) => {
              let v = ChartData.find((d: DataPoint) => d.d === tick)
              if (!v || i === YTicks.length - 1)
                v = {
                  d: tick,
                  v: 12,
                }
              return (
                <YTick
                  lang={lang}
                  key={'ytick_' + i}
                  y={y(new Date(tick))}
                  x1={0}
                  x2={x(v.v || x.range()[1])}
                  label={v.d}
                />
              )
            })}
          </g>
          <SecOutlinePath d={DrawDataLine} />
          <OutlinePath d={DrawDataLine} />
          {pointsToMark.map((date, i) => {
            let v = ChartData.find((d: DataPoint) => d.d === date)
            if (!v || date === currentDate) return null
            return (
              <MarkerCircle
                key={'marker_' + i}
                cx={x(v.v)}
                cy={y(new Date(v.d))}
                r={8}
              />
            )
          })}
        </g>

        {marker && (
          <PointMarker
            lang={lang}
            x={x(marker.v)}
            y={y(new Date(marker.d))}
            value={marker.v}
            date={marker.d}
            position={
              new Date(marker.d) < new Date('2021-06-01') ? 'left' : 'right'
            }
          />
        )}
      </SVG>
      <Source>{lang === 'de' ? "Quelle":"Source"}: Finanzen.ch</Source>
    </ChartRoot>
  )
}
const Source = styled.div`
  font-size: 12px;
  color: var(--gray-3);
  margin-bottom: 60px;
  @media screen and (max-width: 711px) {
    padding-left: 20px;
  }
`
const MarkerCircle = styled.circle`
  stroke: var(--text-color);
  fill: var(--text-color);
  fill-opacity: 0.2;
  stroke-width: 1;
`

const ChartRoot = styled.div`
  width: 100%;
  position: relative;
`

const SVG = styled.svg`
  width: 100%;
  overflow: visible;
`

const XKey = styled.div`
  width: 100%;
  position: fixed;
  top: 2%;
  height: 50px;
  z-index: 1000;
  padding-bottom: 15px;
  pointer-events: none;
`
const KeyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  display: inline-block;
  padding: 0 10px 10px 12px;
  line-height: 1.2em;
  width: 100%;
  @media screen and (max-width: 711px) {
    margin-left: 0px;
    width: calc(100% - 40px);
  }
`

const AreaPath = styled.path`
  fill: var(--brandblue-bright-4);
  stroke: none;
`
const SecOutlinePath = styled.path`
  fill: none;
  stroke: var(--site-background);
  stroke-width: 5;
  stroke-linejoin: round;
  stroke-linecap: round;
`
const OutlinePath = styled.path`
  fill: none;
  stroke: var(--red-main);
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
`
